import { FC, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import classnames from 'classnames';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { DiscountKind } from './useDiscountExclusivity';

export interface DiscountChoiceOption {
  kind: DiscountKind;
  /** Human label — offer title or the promo code. */
  label: string;
  /** € this option saves, when known. Drives the "best choice" badge. */
  saving?: number | null;
}

interface Props {
  /** The discount already applied that applying `candidate` would displace. */
  active: DiscountChoiceOption;
  /** The discount the customer is now considering (this modal's discount). */
  candidate: DiscountChoiceOption;
  /** Apply `candidate`, displacing `active`. */
  onUseInstead: () => void;
  /** Keep `active`; dismiss without applying `candidate`. */
  onKeep: () => void;
  /**
   * Single-action variant (offer modal, promo/gift already active): one sentence naming the active
   * discount + only the "Use offer instead" button — no Keep button, no comparison/best-choice
   * lines (closing the window keeps the code). CTO 2026-08-03.
   */
  singleAction?: boolean;
  /**
   * Replaces the derived active/candidate/"only one" lines with a caller-supplied sentence — used
   * for the multiple-active-offers case ("You have {n} active offers: … Promo code X saves …"),
   * item 6. The Keep/Use buttons + best-choice badge still render.
   */
  messageOverride?: ReactNode;
  className?: string;
}

const hasSaving = (v?: number | null): v is number => typeof v === 'number' && v > 0;

/**
 * Comparison-choice card (PROD-407 Inc 4, FOO-2567).
 *
 * Shown in the offer / promo detail modals when a conflicting discount is already applied.
 * Full mutual exclusivity means only one of {offer, promo, gift, points} can apply, so instead
 * of silently blocking or silently displacing, we let the customer choose — and flag the higher
 * saving as the best choice. The buttons only call back; the caller owns apply/displace so the
 * FE order state stays the single source of truth (no direct engine mutation here).
 */
const DiscountComparisonChoice: FC<Props> = ({
  active,
  candidate,
  onUseInstead,
  onKeep,
  singleAction,
  messageOverride,
  className,
}) => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:DiscountExclusivity.comparison' });
  const { formatCurrency } = useAppMethods();

  // Single-action variant: name the active discount (+ its € saving) in one sentence, and show only
  // the "Use offer instead" button. Only ever used with an active promo/gift in the offer modal.
  if (singleAction && (active.kind === 'promo' || active.kind === 'gift')) {
    const activeNamed = hasSaving(active.saving)
      ? t(`active_named.${active.kind}`, { name: active.label, saving: formatCurrency(active.saving) })
      : t(`active_named_no_saving.${active.kind}`, { name: active.label });

    return (
      <div
        data-test="comparison-choice"
        data-single="true"
        className={classnames('rounded-lg border-xs border-warning/40 bg-warning/5 p-sm space-y-sm', className)}
      >
        <div className="flex items-start gap-xs">
          <AlertTriangle className="w-4 h-4 shrink-0 text-warning mt-[1px]" />
          <p className="text-xs text-brand-text-primary">{t('single_message', { active: activeNamed })}</p>
        </div>
        <button
          type="button"
          onClick={onUseInstead}
          data-test="comparison-use-instead"
          className="w-full rounded-full bg-brand-primary px-sm py-sm text-sm font-semibold text-brand-button-primary-text transition-all hover:opacity-90"
        >
          {t('use_instead', { label: t(`button_kinds.${candidate.kind}`) })}
        </button>
      </div>
    );
  }

  // Best choice = the higher saving, but only when BOTH figures are known. On a tie (or either
  // unknown) we show no badge rather than nudge the customer to churn their active discount.
  const best: 'active' | 'candidate' | null = useMemo(() => {
    if (!hasSaving(active.saving) || !hasSaving(candidate.saving)) return null;
    if (candidate.saving > active.saving) return 'candidate';
    if (active.saving > candidate.saving) return 'active';

    return null;
  }, [active.saving, candidate.saving]);

  const kindNoun = (kind: DiscountKind) => t(`kinds.${kind}`);
  // Keep/Use buttons name the discount by its kind ("Keep offer" / "Use code instead"), matching
  // the prototype — not the raw code value.
  const buttonLabel = (o: DiscountChoiceOption) => t(`button_kinds.${o.kind}`);

  const activeLine = hasSaving(active.saving)
    ? t('active_line', { kind: kindNoun(active.kind), name: active.label, saving: formatCurrency(active.saving) })
    : t('active_line_no_saving', { kind: kindNoun(active.kind), name: active.label });

  return (
    <div
      data-test="comparison-choice"
      className={classnames('rounded-lg border-xs border-warning/40 bg-warning/5 p-sm space-y-sm', className)}
    >
      <div className="flex items-start gap-xs">
        <AlertTriangle className="w-4 h-4 shrink-0 text-warning mt-[1px]" />
        {messageOverride ? (
          <p className="text-xs text-brand-text-primary" data-test="comparison-message-override">
            {messageOverride}
          </p>
        ) : (
          <p className="text-xs text-brand-text-primary space-x-1">
            <span data-test="comparison-active">{activeLine}</span>
            {hasSaving(candidate.saving) && (
              <span data-test="comparison-candidate">
                {t('candidate_line', { kind: kindNoun(candidate.kind), saving: formatCurrency(candidate.saving) })}
              </span>
            )}
            <span>{t('only_one')}</span>
          </p>
        )}
      </div>
      {/* Keep = white outline, Use-instead = filled primary (the action), per the prototype. The
          green BEST CHOICE badge floats above whichever option saves more. Stacked on mobile so
          the labels never wrap, side-by-side from `sm` up. */}
      <div className="flex flex-col sm:flex-row gap-xs mt-1">
        <ChoiceButton
          data-test="comparison-keep"
          label={t('keep', { label: buttonLabel(active) })}
          badge={best === 'active' ? t('best_choice') : null}
          onClick={onKeep}
          filled={false}
        />
        <ChoiceButton
          data-test="comparison-use-instead"
          label={t('use_instead', { label: buttonLabel(candidate) })}
          badge={best === 'candidate' ? t('best_choice') : null}
          onClick={onUseInstead}
          filled
        />
      </div>
    </div>
  );
};

interface ChoiceButtonProps {
  label: string;
  badge: string | null;
  onClick: () => void;
  filled: boolean;
  'data-test': string;
}

const ChoiceButton: FC<ChoiceButtonProps> = ({ label, badge, onClick, filled, ...rest }) => (
  <div className="relative flex-1">
    {badge && (
      <span
        data-test="comparison-best-badge"
        className="absolute -top-2 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-success px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
      >
        {badge}
      </span>
    )}
    <button
      type="button"
      onClick={onClick}
      data-test={rest['data-test']}
      className={classnames(
        'w-full rounded-full px-sm py-sm text-sm font-semibold transition-all',
        filled
          ? 'bg-brand-primary text-brand-button-primary-text hover:opacity-90'
          : 'bg-white border-xs border-brand-border text-brand-text-primary hover:bg-muted/40',
      )}
    >
      {label}
    </button>
  </div>
);

export default DiscountComparisonChoice;
